import { FC, ReactNode, useEffect, useId, useRef } from 'react';
import cx from 'classnames';

import { CloseButton } from '../close-button';
import { Heading } from '../heading';
import { ModalWrap } from '../modal-wrap';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  isOpen: boolean;
  /** The cross and Escape. Omit it for a window that can only be left through its own buttons. */
  onClose?: () => void;
  title?: ReactNode;
  description?: ReactNode;
  /** Icon buttons in the title row, before the cross. */
  headerActions?: ReactNode;
  /** 420 / 600 / 720 / 960px wide. */
  size?: ModalSize;
  /** The action row under the content. */
  footer?: ReactNode;
  /** Fills the screen below `sm` — for long forms. */
  fullscreenOnMobile?: boolean;
  /** Below `sm` the window is a sheet pinned to the bottom edge — for short questions and pickers. */
  sheetOnMobile?: boolean;
  /** `inverse` — a dark window over the meet's dark tools. */
  tone?: 'default' | 'inverse';
  /** Off for forms with pickers that use Escape themselves. */
  closeOnEscape?: boolean;
  /** A click on the backdrop closes too — only for windows with nothing typed in to lose. */
  closeOnBackdrop?: boolean;
  children?: ReactNode;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-[420px] p-6',
  md: 'max-w-[600px] p-6 lg:p-10',
  lg: 'max-w-[720px] p-6 lg:p-10',
  xl: 'max-w-[960px] p-6 lg:p-10',
};

// Escape closes only the window on top.
const openModals: symbol[] = [];

/** The one window of the app: backdrop, panel, title, cross, Escape and scroll lock. */
export const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  headerActions,
  size = 'md',
  footer,
  fullscreenOnMobile = false,
  sheetOnMobile = false,
  tone = 'default',
  closeOnEscape = true,
  closeOnBackdrop = false,
  children,
}) => {
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const canEscape = closeOnEscape && Boolean(onClose);

  useEffect(() => {
    if (!isOpen) return;

    const token = Symbol('modal');
    openModals.push(token);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !canEscape) return;
      if (openModals[openModals.length - 1] !== token) return;
      onCloseRef.current?.();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      openModals.splice(openModals.indexOf(token), 1);
    };
  }, [isOpen, canEscape]);

  return (
    <ModalWrap
      isShow={isOpen}
      className={cx((fullscreenOnMobile || sheetOnMobile) && 'max-sm:p-0')}
      onBackdropClick={closeOnBackdrop ? onClose : undefined}
    >
      {/* The backdrop scrolls, not the window: a dropdown inside may then hang out of the window unclipped. */}
      <div
        className="think-scroll flex h-full w-full overflow-y-auto"
        onMouseDown={(event) => {
          if (closeOnBackdrop && event.target === event.currentTarget) onClose?.();
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          className={cx(
            'm-auto flex w-full flex-col gap-6 rounded-card shadow-raised',
            tone === 'inverse' ? 'bg-surface-inverse text-white' : 'bg-surface text-ink',
            sizeClasses[size],
            fullscreenOnMobile && 'max-sm:min-h-full max-sm:max-w-none max-sm:rounded-none',
            sheetOnMobile &&
              'max-sm:mb-0 max-sm:max-w-none max-sm:rounded-b-none max-sm:pb-[calc(1.5rem+env(safe-area-inset-bottom))]',
          )}
          // Panels that close on an outside click must not treat a click in the window as one.
          onClick={(event) => event.stopPropagation()}
        >
          {(title || onClose || headerActions) && (
            <div className="flex w-full items-start justify-between gap-4">
              <div className="flex min-w-0 flex-col gap-1.5 break-words">
                {title && (
                  <Heading id={titleId} size="lg" tone={tone}>
                    {title}
                  </Heading>
                )}
                {description && (
                  <p className={cx('text-body-s', tone === 'inverse' ? 'text-white/70' : 'text-ink-muted')}>
                    {description}
                  </p>
                )}
              </div>
              {(headerActions || onClose) && (
                <div className="-m-1.5 ml-auto flex shrink-0 items-center gap-2">
                  {headerActions}
                  {onClose && <CloseButton tone={tone} onClick={onClose} />}
                </div>
              )}
            </div>
          )}
          <div className="min-w-0">{children}</div>
          {footer && <div className="flex flex-wrap justify-end gap-4">{footer}</div>}
        </div>
      </div>
    </ModalWrap>
  );
};
